import React, { useMemo } from 'react';
import { Stack } from 'new/elements/Stack';
import { ConfigBasic } from 'new/components/settings/VendorBuilder/AuthConfig/ConfigBasic';
import {
  includeModeTypes,
  requestModes,
  responseFormats,
} from 'new/components/settings/VendorBuilder/selectTypes';
import { FormBuilder } from 'new/components/settings/VendorBuilder/FormControlBuilder';

interface TokenProps {
  index: number;
}

const fields = (index) => [
  {
    type: 'text',
    required: true,
    label: (
      <>
        Fetch Token URL <code>config["token.auth.url"]</code>
      </>
    ),
    description: 'URL to get the token',
    name: `auth_templates[${index}].config["token.auth.url"]`,
  },
  {
    type: 'grid',
    gridDirection: 'column',
    fields: [
      {
        type: 'select',
        required: true,
        label: (
          <>
            Token URL: HTTP method <code>config["token.auth.method"]</code>
          </>
        ),
        defaultValue: requestModes[1].value,
        items: requestModes,
        name: `auth_templates[${index}].config["token.auth.method"]`,
      },
      {
        type: 'text',
        label: (
          <>
            Token URL: Header name for user/pwd
            <code>config["token.auth.token.header.name"]</code>
          </>
        ),
        required: true,
        name: `auth_templates[${index}].config["token.auth.token.header.name"]`,
        defaultValue: 'Authorization',
      },
    ],
  },
  {
    type: 'text',
    required: true,
    label: (
      <>
        Token URL: Request Body (JSON) <code>config["token.auth.body"]</code>
      </>
    ),
    multiline: true,
    name: `auth_templates[${index}].config["token.auth.body"]`,
  },
  {
    type: 'grid',
    gridDirection: 'column',
    fields: [
      {
        type: 'select',
        required: true,
        label: (
          <>
            Token URL: Response format <code>config["token.auth.response.format"]</code>
          </>
        ),
        defaultValue: responseFormats[1].value,
        items: responseFormats,
        name: `auth_templates[${index}].config["token.auth.response.format"]`,
      },
      {
        type: 'text',
        required: true,
        label: (
          <>
            Token URL Response: Path to Token{' '}
            <code>
              <code>config["token.auth.token.path"]</code>
            </code>
          </>
        ),
        name: `auth_templates[${index}].config["token.auth.token.path"]`,
        helperText: 'Required if format is XML or JSON',
      },
    ],
  },
  {
    type: 'select',
    required: true,
    label: (
      <>
        Where to add token on API requests <code>config["token.auth.token.include.mode"]</code>
      </>
    ),
    defaultValue: includeModeTypes[0].value,
    items: includeModeTypes,
    name: `auth_templates[${index}].config["token.auth.token.include.mode"]`,
  },
  {
    dependsOn: {
      [`auth_templates[${index}].config['token.auth.token.include.mode']`]: 'URL_PARAMETER',
    },
    type: 'text',
    required: true,
    label: (
      <>
        URL Parameter for Token in API Requests
        <code>config["token.auth.token.url.parameter</code>
      </>
    ),
    name: `auth_templates[${index}].config["token.auth.token.url.parameter"]`,
  },
  {
    dependsOn: {
      [`auth_templates[${index}].config['token.auth.token.include.mode']`]: 'HEADER',
    },
    type: 'text',
    required: true,
    label: (
      <>
        Header name for Token in API Requests <code>config["token.auth.request.header.name"]</code>
      </>
    ),
    defaultValue: 'Authorization',
    name: `auth_templates[${index}].config["token.auth.request.header.name"]`,
  },
  {
    dependsOn: {
      [`auth_templates[${index}].config['token.auth.token.include.mode']`]: 'HEADER',
    },
    type: 'text',
    required: true,
    defaultValue: 'Bearer',
    label: (
      <>
        Header Prefix for Token in API Requests{' '}
        <code>config["token.auth.request.header.prefix"]</code>
      </>
    ),
    name: `auth_templates[${index}].config["token.auth.request.header.prefix"]`,
  },
  {
    dependsOn: {
      [`auth_templates[${index}].config['token.auth.token.include.mode']`]: 'HEADER',
    },
    type: 'checkbox',
    required: true,
    label: (
      <>
        Send cookie in a header? <code>config["token.auth.token.include.cookie"]</code>
      </>
    ),
    name: `auth_templates[${index}].config["token.auth.token.include.cookie"]`,
  },
];

export const ConfigToken = (props: TokenProps) => {
  const { index } = props;

  const tokenFields = useMemo(() => fields(index), [index]);

  return (
    <Stack gap="16px">
      <ConfigBasic {...props} />
      <FormBuilder fields={tokenFields} />
    </Stack>
  );
};
